'use client';

import { c as _c } from "react/compiler-runtime";
import { useIsFetching } from '@tanstack/react-query';
import { useLocale } from 'next-intl';
import { TRPC_SETTINGS } from '~/constants/trpc';
import type { Locale } from '~/i18n/i18n-config';
import { api } from '~/libs/api';
import { useTripBookingStore } from '~/stores/tpv2/tripBookingStore';
import { convertPrice } from '~/utils/tpv2/currency';
import { isTripRepriceInProgress, TRIP_REPRICE_POLL_MS, tripRepriceKey } from '~/utils/tpv2/tripReprice';
import { useTripOverviewSummary } from './useTripOverviewSummary';
export const useTripPriceSummary = () => {
  const $ = _c(23);
  const {
    tripId,
    isLiveChecking
  } = useTripOverviewSummary();
  const locale = useLocale() as Locale;
  const displayCurrency = useTripBookingStore(_temp);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: exchangeRates
  } = api.account.getCurrentExchangeRates.useQuery(t0, TRPC_SETTINGS.QUERY_NO_REFETCH);
  let t1;
  if ($[1] !== displayCurrency || $[2] !== exchangeRates?.exchangeRates) {
    t1 = exchangeRates?.exchangeRates.find(r => r.quoteCurrency === displayCurrency)?.exchangeRate ?? 1;
    $[1] = displayCurrency;
    $[2] = exchangeRates?.exchangeRates;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const rate = t1;
  let t2;
  if ($[4] !== tripId) {
    t2 = tripRepriceKey(tripId);
    $[4] = tripId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const isRepricing = useIsFetching(t2) > 0;
  let t3;
  if ($[6] !== tripId) {
    t3 = {
      tripId
    };
    $[6] = tripId;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = !!tripId && !isRepricing && !isLiveChecking;
  let t5;
  if ($[8] !== tripId) {
    t5 = () => isTripRepriceInProgress(tripId) ? TRIP_REPRICE_POLL_MS : false;
    $[8] = tripId;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = {
      ...TRPC_SETTINGS.QUERY_NO_REFETCH,
      enabled: t4,
      refetchOnMount: "always",
      refetchInterval: t5
    };
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const {
    data: priceData,
    isError
  } = api.trip.getPrice.useQuery(t3, t6);
  const totalPrice = isLiveChecking ? undefined : priceData?.totalPrepayAmount.amount;
  const isPriceLoading = totalPrice === undefined && !isError && !!tripId;
  let t7;
  if ($[13] !== displayCurrency || $[14] !== locale || $[15] !== rate) {
    t7 = amount => convertPrice(amount, rate, locale, displayCurrency);
    $[13] = displayCurrency;
    $[14] = locale;
    $[15] = rate;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const formatPrice = t7;
  let t8;
  if ($[17] !== displayCurrency || $[18] !== formatPrice || $[19] !== isPriceLoading || $[20] !== rate || $[21] !== totalPrice) {
    t8 = {
      totalPrice,
      rate,
      formatPrice,
      displayCurrency,
      isPriceLoading
    };
    $[17] = displayCurrency;
    $[18] = formatPrice;
    $[19] = isPriceLoading;
    $[20] = rate;
    $[21] = totalPrice;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
};
function _temp(s) {
  return s.displayCurrency;
}